import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import PlanDrawer, { PlanDrawerProps } from "../PlanDrawer/PlanDrawer";
import { PlanType } from "../../../apis/subscriptionApi";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import PlanCategoryDrawer, {
  PlanCategoryDrawerProps,
} from "../PlanCategoryDrawer/PlanCategoryDrawer";
import {
  PlanTypeWithTemplatesModal,
  PlanTypeWithTemplatesModalProps,
} from "../PlanTemplates/PlanTypeWithTemplatesModal";
import { PlanTemplate } from "../PlanTemplates/plan-templates";
import { useExperiment } from "../../../hooks/useExperiment";
import PlanV2Modal from "../PlanDrawer/PlanV2Modal";

// internal hook
function useProvider() {
  const { trackEvent } = useAnalytics();

  const { useExperimentValue } = useExperiment();

  const isCreatePlanModalEnabled = useExperimentValue(
    "plan-v2-feature_flag",
    false,
  );

  const [planTypeModalProps, setPlanTypeModalProps] =
    useState<PlanTypeWithTemplatesModalProps>(() => ({
      visible: false,
      onClose: () => {
        setPlanTypeModalProps((prev) => ({ ...prev, visible: false }));
      },
    }));

  const [planDrawerProps, setPlanDrawerProps] = useState<PlanDrawerProps>(
    () => ({
      visible: false,
      onClose: () => {
        setPlanDrawerProps((prev) => ({
          ...prev,
          visible: false,
          planUuid: undefined,
          planType: undefined,
          duplicate: undefined,
        }));
      },
    }),
  );

  const [planCategoryDrawerProps, setPlanCategoryDrawerProps] =
    useState<PlanCategoryDrawerProps>(() => ({
      visible: false,
      onClose: () => {
        setPlanCategoryDrawerProps((prev) => ({
          ...prev,
          visible: false,
          planCategoryUuid: undefined,
        }));
      },
    }));

  const startPlanCreation = useCallback(
    ({ onSuccess }: { onSuccess: VoidFunction }) => {
      setPlanTypeModalProps({
        visible: true,
        onClose: () =>
          setPlanTypeModalProps((prev) => ({ ...prev, visible: false })),
        onSuccess,
      });
      trackEvent("Plan Create Clicked");
    },
    [trackEvent],
  );

  const startPlanEdition = useCallback(
    ({
      planUuid,
      onSuccess,
    }: {
      planUuid: string;
      onSuccess: VoidFunction;
    }) => {
      setPlanDrawerProps((prev) => ({
        ...prev,
        visible: true,
        planUuid,
        onSuccess,
      }));
      trackEvent("Plan Edit Clicked");
    },
    [trackEvent],
  );

  const startPlanDuplication = useCallback(
    ({ planUuid }: { planUuid: string; onSuccess: VoidFunction }) => {
      setPlanDrawerProps((prev) => ({
        ...prev,
        visible: true,
        planUuid,
        duplicate: true,
      }));
      trackEvent("Plan Duplicate Clicked");
    },
    [trackEvent],
  );

  const openPlanDrawer = useCallback(
    ({
      planType,
      onSuccess,
      planTemplate,
    }: {
      planType: PlanType;
      onSuccess: VoidFunction;
      planTemplate?: PlanTemplate;
    }) => {
      setPlanDrawerProps((prev) => ({
        ...prev,
        visible: true,
        planType,
        onSuccess,
        planTemplate,
      }));

      trackEvent("Plan Drawer Opened", {
        plan_type: planType,
        from_template: !!planTemplate,
        template_id: planTemplate?.id,
      });
    },
    [trackEvent],
  );

  const startPlanCategoryCreation = useCallback(
    ({ onSuccess }: { onSuccess: VoidFunction }) => {
      setPlanCategoryDrawerProps({
        visible: true,
        onClose: () =>
          setPlanCategoryDrawerProps((prev) => ({ ...prev, visible: false })),
        onSuccess,
      });
    },
    [],
  );

  const startPlanCategoryEdition = useCallback(
    ({
      planCategoryUuid,
      onSuccess,
    }: {
      planCategoryUuid: string;
      onSuccess: VoidFunction;
    }) => {
      setPlanCategoryDrawerProps({
        visible: true,
        planCategoryUuid,
        onClose: () =>
          setPlanCategoryDrawerProps((prev) => ({ ...prev, visible: false })),
        onSuccess,
      });
    },
    [],
  );

  const contextValue = useMemo(() => {
    return {
      startPlanCreation,
      openPlanDrawer,
      startPlanEdition,
      startPlanDuplication,
      startPlanCategoryCreation,
      startPlanCategoryEdition,
    };
  }, [
    startPlanCreation,
    openPlanDrawer,
    startPlanEdition,
    startPlanDuplication,
    startPlanCategoryCreation,
    startPlanCategoryEdition,
  ]);

  return {
    contextValue,
    planTypeModalProps,
    planDrawerProps,
    planCategoryDrawerProps,
    isCreatePlanModalEnabled,
  };
}

// Create the context
const PlansContext = createContext<
  ReturnType<typeof useProvider>["contextValue"] | undefined
>(undefined);

// Create the provider component
const PlansProvider = ({ children }: { children?: ReactNode }) => {
  const {
    contextValue,
    planTypeModalProps,
    planDrawerProps,
    planCategoryDrawerProps,
    isCreatePlanModalEnabled,
  } = useProvider();
  return (
    <PlansContext.Provider value={contextValue}>
      {children}
      <PlanTypeWithTemplatesModal
        visible={planTypeModalProps.visible}
        onClose={planTypeModalProps.onClose}
        onSuccess={planTypeModalProps.onSuccess}
      />
      {isCreatePlanModalEnabled && <PlanV2Modal {...planDrawerProps} />}
      {!isCreatePlanModalEnabled && <PlanDrawer {...planDrawerProps} />}
      <PlanCategoryDrawer {...planCategoryDrawerProps} />
    </PlansContext.Provider>
  );
};

// Create a hook to use the context
export function usePlans() {
  const context = useContext(PlansContext);
  if (!context) {
    throw new Error("usePlans must be used within a PlansProvider");
  }
  return context;
}

export default PlansProvider;
